<script setup>
import { onMounted, ref } from "vue";
import router from "@/router";
import { permissionsStore } from "@/stores/permissions";
const permissions = permissionsStore();

const dashboardItem = ref(null);
const intuneItems = ref([]);
const jamfItems = ref([]);

permissions.$subscribe(() => {
  buildSidebarItems();
  loadFirstPage();
});

const dashboardDef = {
  title: "Dashboard",
  icon: "mdi-view-dashboard-outline",
  link: "MdmToolsDashboard",
  privName: "MdmToolsAll",
};

const allIntuneItems = [
  {
    title: "Deploy",
    icon: "mdi-import",
    link: "MdmToolsDeployment",
    privName: "MdmToolsAll",
  },
  {
    title: "Export",
    icon: "mdi-export",
    link: "MdmToolsExport",
    privName: "MdmToolsAll",
  },
  {
    title: "Collection Editor",
    icon: "mdi-bookmark-box-multiple-outline",
    link: "MdmToolsCollectionEditor",
    privName: "MdmToolsAll",
  },
  {
    title: "Config Editor",
    icon: "mdi-file-edit-outline",
    link: "MdmToolsConfigEditor",
    privName: "MdmToolsAll",
  },
  {
    title: "Security Audit",
    icon: "mdi-security",
    link: "MdmToolsSecurityAudit",
    privName: "MdmToolsAll",
  },
];

const allJamfItems = [
  {
    title: "Import",
    icon: "mdi-import",
    link: "MdmToolsJamfExport",
    privName: "MdmToolsAll",
  },
  {
    title: "Deploy",
    icon: "mdi-import",
    link: "MdmToolsJamfDeploy",
    privName: "MdmToolsAll",
  },
  {
    title: "Staged",
    icon: "mdi-progress-clock",
    link: "MdmToolsJamfStaged",
    privName: "MdmToolsAll",
  },
  {
    title: "Products",
    icon: "mdi-package-variant-closed",
    link: "MdmToolsJamfProductEditor",
    privName: "MdmToolsAll",
  },
  {
    title: "Catalog",
    icon: "mdi-book-open-variant",
    link: "MdmToolsJamfCatalog",
    privName: "MdmToolsAll",
  },
  {
    title: "Config Report",
    icon: "mdi-file-chart-outline",
    link: "MdmToolsJamfConfigReport",
    privName: "MdmToolsAll",
  },
];

function buildSidebarItems() {
  console.log(permissions.$state);
  dashboardItem.value = permissions.$state["MdmTools-" + dashboardDef.privName]
    ? dashboardDef
    : null;
  intuneItems.value = allIntuneItems.filter(
    (item) => permissions.$state["MdmTools-" + item.privName]
  );
  jamfItems.value = allJamfItems.filter(
    (item) => permissions.$state["MdmTools-" + item.privName]
  );
}

onMounted(async () => {
  buildSidebarItems();
  loadFirstPage();
});

function loadFirstPage() {
  if (!router.currentRoute.value.name) {
    if (dashboardItem.value) {
      router.push({ name: dashboardItem.value.link });
    } else if (intuneItems.value.length > 0) {
      router.push({ name: intuneItems.value[0].link });
    } else if (jamfItems.value.length > 0) {
      router.push({ name: jamfItems.value[0].link });
    }
  }
}

// Required to make the router-view reload when user clicks the link again
// See the key value set on <router-view> below.
const randomKey = ref("");
function updateRandom() {
  randomKey.value = (Math.random() + 1).toString(36).substring(7);
}
</script>

<template>
  <v-navigation-drawer permanent width="190" theme="jigsawDark">
    <v-list density="compact">
      <div class="v-list-item">
        <v-icon class="pa-3" color="#e7541e">mdi-tools</v-icon>
        <div class="text-h7 pl-2 cursor-default">
          <strong>MDM Tools</strong>
        </div>
      </div>

      <v-list-item
        v-if="dashboardItem"
        link
        :to="{ name: dashboardItem.link }"
        @click="updateRandom()"
        class="sideBarListItem"
      >
        <template v-slot:prepend>
          <v-icon>{{ dashboardItem.icon }}</v-icon>
        </template>
        <v-list-item-title class="pa-0">
          {{ dashboardItem.title }}
        </v-list-item-title>
      </v-list-item>

      <v-list-group v-if="intuneItems.length" value="intune">
        <template v-slot:activator="{ props }">
          <v-list-item v-bind="props" class="sideBarGroupHeader">
            <template v-slot:prepend>
              <v-icon size="small" color="#e7541e">mdi-microsoft</v-icon>
            </template>
            <v-list-item-title class="pa-0 font-weight-bold">
              Intune
            </v-list-item-title>
          </v-list-item>
        </template>
        <v-list-item
          v-for="item in intuneItems"
          :key="item.title"
          link
          :to="{ name: item.link }"
          @click="updateRandom()"
          class="sideBarListItem"
        >
          <template v-slot:prepend>
            <v-icon>{{ item.icon }}</v-icon>
          </template>
          <v-list-item-title class="pa-0">
            {{ item.title }}
          </v-list-item-title>
        </v-list-item>
      </v-list-group>

      <v-list-group v-if="jamfItems.length" value="jamf">
        <template v-slot:activator="{ props }">
          <v-list-item v-bind="props" class="sideBarGroupHeader">
            <template v-slot:prepend>
              <v-icon size="small" color="#e7541e">mdi-apple</v-icon>
            </template>
            <v-list-item-title class="pa-0 font-weight-bold">
              Jamf
            </v-list-item-title>
          </v-list-item>
        </template>
        <v-list-item
          v-for="item in jamfItems"
          :key="item.title"
          link
          :to="{ name: item.link }"
          @click="updateRandom()"
          class="sideBarListItem"
        >
          <template v-slot:prepend>
            <v-icon>{{ item.icon }}</v-icon>
          </template>
          <v-list-item-title class="pa-0">
            {{ item.title }}
          </v-list-item-title>
        </v-list-item>
      </v-list-group>
    </v-list>
  </v-navigation-drawer>
  <v-main>
    <v-container fluid>
      <router-view :key="randomKey" />
    </v-container>
  </v-main>
</template>

<style scoped>
.sideBarListItem :deep(.v-list-item__prepend .v-list-item__spacer) {
  width: 10px !important;
}
.sideBarGroupHeader :deep(.v-list-item__prepend .v-list-item__spacer) {
  width: 10px !important;
}
:deep(.v-list-group__items .v-list-item) {
  padding-inline-start: 16px !important;
}
</style>
